<template>
  <div class="skill-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>$attrs 与 $listeners</h2>
      <p>Vue 2 组件透传 — 父组件未声明为 props/emit 的属性与事件</p>
    </div>

    <!-- 概念说明 -->
    <div class="concept-box">
      <strong>💡 核心概念</strong><br>
      • <code>$attrs</code> — 父作用域中不作为 props 被识别的 attribute 绑定（class/style 除外）<br>
      • <code>$listeners</code> — 父作用域中不作为组件事件被识别的 v-on 事件监听器<br>
      • 配合 <code>v-bind="$attrs"</code> + <code>v-on="$listeners"</code> 可实现 <strong>透明包装组件</strong>
    </div>

    <!-- ===== 演示 ===== -->
    <!-- 祖先组件 -->
    <div class="comp-box">
      <span class="layer layer-grandparent">🏠 祖先 (App)</span>
      <div class="comp-title">向下传递属性 &amp; 事件</div>
      <div class="flex-row">
        <label>自定义属性（透传）<br>
          <input v-model="attrsToPass.placeholder" placeholder="placeholder">
        </label>
        <label>自定义属性（透传）<br>
          <input v-model="attrsToPass.title" placeholder="title">
        </label>
        <label>自定义属性<br>
          <select v-model="attrsToPass.size">
            <option value="">size (不传)</option>
            <option value="large">large</option>
            <option value="small">small</option>
          </select>
        </label>
      </div>
      <p class="click-count">
        点击次数：<strong>{{ clickCount }}</strong>
      </p>

      <!-- 调用父组件 -->
      <my-parent
        v-bind="attrsToPass"
        @custom-event="handleCustomEvent"
        @child-click="handleChildClick"
        @focus="handleFocus"
      />
    </div>

    <!-- ===== 解析面板 ===== -->
    <div class="props-panel">
      <h4>📋 透传详情</h4>
      <div class="panel-grid">
        <div class="panel-col">
          <span class="tag">祖先 → 父</span> 传递的 attributes：
          <pre>{{ JSON.stringify(attrsToPass, null, 2) }}</pre>
        </div>
        <div class="panel-col">
          <span class="tag">祖先</span> 绑定的事件：
          <pre>@custom-event  @child-click  @focus</pre>
        </div>
      </div>
      <div class="event-log" ref="eventLog">
        <div v-for="(log, i) in eventLogs" :key="i" class="log-entry">{{ log }}</div>
        <div v-if="eventLogs.length === 0" class="log-empty">暂无事件 — 点击下方按钮触发</div>
      </div>
    </div>

    <div class="attribution">
      Vue 2 案例 · <code>$attrs</code> + <code>$listeners</code> 透传演示
    </div>
  </div>
</template>

<script>
import MyParent from './components/MyParent.vue'

export default {
  name: 'AttrsListeners',
  components: {
    MyParent
  },
  data() {
    return {
      attrsToPass: {
        placeholder: '请输入内容...',
        title: '这是一个透传 title',
        size: 'large'
      },
      clickCount: 0,
      eventLogs: []
    }
  },
  methods: {
    handleCustomEvent(payload) {
      this.clickCount++
      this.addLog(`custom-event 触发 → ${JSON.stringify(payload)}`)
    },
    handleChildClick(msg) {
      this.clickCount++
      this.addLog(`child-click 触发 → ${msg}`)
    },
    handleFocus(e) {
      this.addLog(`focus 事件触发 → input 获得焦点`)
    },
    addLog(msg) {
      const now = new Date()
      const time = now.toLocaleTimeString('zh-CN', { hour12: false })
      this.eventLogs.push(`[${time}] ${msg}`)
      this.$nextTick(() => {
        const el = this.$refs.eventLog
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.concept-box {
  background: #e8f4fd;
  border-left: 4px solid #1890ff;
  padding: 16px 20px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.7;
}

.concept-box code {
  background: #d5e8f7;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.layer {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 10px;
  margin-bottom: 10px;
}

.layer-grandparent {
  background: #fff1f0;
  color: #cf1322;
}

.comp-box {
  border: 2px dashed #d9d9d9;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
  transition: border-color .25s;
  background: var(--surface-1);
}

.comp-title {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 15px;
}

.comp-box label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #555;
}

.comp-box input,
.comp-box select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border .2s;
}

.comp-box input:focus,
.comp-box select:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, .2);
}

.flex-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.flex-row > * {
  flex: 1;
  min-width: 180px;
}

.click-count {
  margin: 8px 0 4px;
  font-size: 13px;
  color: #888;
}

.props-panel {
  background: var(--surface-1);
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13px;
  margin-top: 12px;
}

.props-panel h4 {
  font-size: 13px;
  color: #888;
  margin-bottom: 6px;
  font-weight: 600;
}

.props-panel pre {
  font-family: 'Cascadia Code', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  background: var(--surface-1);
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
  padding: 10px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.panel-grid {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.panel-col {
  flex: 1;
  min-width: 200px;
}

.tag {
  display: inline-block;
  background: #e6f7ff;
  color: #1890ff;
  padding: 0 6px;
  border-radius: 3px;
  font-size: 11px;
  margin: 2px 2px 2px 0;
}

.event-log {
  max-height: 100px;
  overflow-y: auto;
  font-size: 12px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 8px 12px;
  margin-top: 8px;
}

.log-entry {
  color: #ad6800;
}

.log-entry::before {
  content: '⚡ ';
}

.log-empty {
  color: #bbb;
}

.attribution {
  font-size: 12px;
  color: #aaa;
  text-align: center;
  margin-top: 32px;
}
</style>
